import { useState, type PropsWithChildren } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { LoginWizardDescriptionContext } from '../../../context/LoginWizardDescriptionContext';
import { useHostedAuthSession } from '../../../hooks/useHostedAuthSession';
import { useProvider } from '../../../hooks/useProvider';
import { WizardStepWrapper } from './WizardStepContainer';

export function LoginWizardContainer({ children }: PropsWithChildren<object>) {
  const { data: session } = useHostedAuthSession();
  const [description, setDescription] = useState<string | null>(null);

  if (!session) return null;

  return (
    <LoginWizardDescriptionContext value={setDescription}>
      <LoginWizardWrapper
        brand_name={session.brand_name}
        brand_icon_url={session.brand_icon_url}
        type={session.account_id ? 'reconnect' : 'connect'}
        description={description}
      >
        {children}
      </LoginWizardWrapper>
    </LoginWizardDescriptionContext>
  );
}

export function LoginWizardWrapper({
  children,
  brand_name,
  brand_icon_url,
  type,
  description,
}: PropsWithChildren<{
  brand_name: string | undefined;
  brand_icon_url: string | undefined;
  type: 'connect' | 'reconnect';
  description?: string | null;
}>) {
  const { provider_name } = useProvider();
  const { t } = useTranslation();

  const title = t('ProviderWizard.eyebrow.' + type, { provider: provider_name });
  const getDescription = () => {
    if (type === 'reconnect') {
      if (brand_name)
        return (
          <Trans i18nKey="ProvidersList.description_reconnect" values={{ app_name: brand_name }}>
            to reconnect your account with <span className="text-primary/70 font-medium">{brand_name}</span>
          </Trans>
        );
      else return t('ProvidersList.description_reconnect_short');
    } else {
      if (brand_name)
        return (
          <Trans i18nKey="ProvidersList.description" values={{ app_name: brand_name }}>
            to connect an account with <span className="text-primary/70 font-medium">{brand_name}</span>
          </Trans>
        );
      else return t('ProvidersList.description_short');
    }
  };

  return (
    <WizardStepWrapper title={title} description={description ?? getDescription()} brand_icon_url={brand_icon_url}>
      {children}
    </WizardStepWrapper>
  );
}
